import type { ReviewedInvoiceFactsInput } from '../../lib/api';

export type VatCategory = 'S' | 'Z' | 'E';

export interface BusinessInvoiceFormValues {
  seller: InvoicePartyFormValues;
  buyer: InvoicePartyFormValues;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  buyerReference: string;
  paymentMeansCode: string;
  description: string;
  quantity: string;
  unitPriceNaira: string;
  unitCode: string;
  vatCategory: VatCategory;
  vatRatePercent: string;
  taxExemptionReason: string;
}

export interface InvoicePartyFormValues {
  registrationName: string;
  identifierScheme: string;
  electronicAddress: string;
  partyId: string;
  companyId: string;
  taxRegistrationId: string;
  streetName: string;
  cityName: string;
  postalZone: string;
  countryCode: string;
}

export interface InvoiceTotalsPreview {
  lineExtensionAmountKobo: string;
  taxAmountKobo: string;
  payableAmountKobo: string;
}

export interface InvoiceResourceView {
  id: string;
  version?: number;
  state: string;
  invoiceNumber: string;
  payableAmountKobo: string;
  payloadSha256: string;
  validationState: string;
  payloadBase64?: string;
}

export interface MockInvoiceReceiptView {
  state: 'MOCK_RECEIVED' | 'MOCK_REJECTED' | 'MOCK_DISPATCH_AMBIGUOUS';
  regulatedPayloadSha256: string;
  validationErrors: string[];
}

export function initialBusinessInvoiceForm(businessName: string): BusinessInvoiceFormValues {
  const today = new Date().toISOString().slice(0, 10);
  return {
    seller: emptyParty(businessName), buyer: emptyParty(''), invoiceNumber: '', issueDate: today, dueDate: today,
    buyerReference: '', paymentMeansCode: '30', description: '', quantity: '1', unitPriceNaira: '', unitCode: 'EA',
    vatCategory: 'S', vatRatePercent: '7.5', taxExemptionReason: '',
  };
}

export function nairaToKoboExact(value: string): string {
  if (!/^(0|[1-9]\d*)(?:\.\d{1,2})?$/.test(value))
    throw new Error('Enter the unit price in naira with no commas and no more than two decimal places.');
  const [whole = '0', fraction = ''] = value.split('.');
  return (BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'))).toString();
}

export function percentageToBasisPoints(value: string): number {
  if (!/^(?:0|[1-9]\d?)(?:\.\d{1,2})?|100(?:\.0{1,2})?$/.test(value))
    throw new Error('Enter a VAT rate from 0 to 100 with no more than two decimal places.');
  const [whole = '0', fraction = ''] = value.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

export function calculateInvoiceTotals(values: Pick<BusinessInvoiceFormValues,
  'quantity' | 'unitPriceNaira' | 'vatCategory' | 'vatRatePercent'>): InvoiceTotalsPreview {
  if (!/^[1-9]\d*$/.test(values.quantity)) throw new Error('Quantity must be a positive whole number.');
  const quantity = BigInt(values.quantity); const unitPrice = BigInt(nairaToKoboExact(values.unitPriceNaira));
  const basisPoints = percentageToBasisPoints(values.vatRatePercent);
  if (values.vatCategory !== 'S' && basisPoints !== 0) throw new Error('Zero-rated and exempt supplies must use a 0% VAT rate.');
  if (values.vatCategory === 'S' && basisPoints === 0) throw new Error('Standard-rated supplies require a VAT rate greater than 0%.');
  const extension = quantity * unitPrice;
  const tax = (extension * BigInt(basisPoints) + 5_000n) / 10_000n;
  return { lineExtensionAmountKobo: extension.toString(), taxAmountKobo: tax.toString(),
    payableAmountKobo: (extension + tax).toString() };
}

export async function buildReviewedInvoiceFacts(values: BusinessInvoiceFormValues, reviewedAt: string): Promise<ReviewedInvoiceFactsInput> {
  required(values.invoiceNumber, 'Invoice number'); required(values.buyerReference, 'Buyer reference');
  required(values.description, 'Description'); required(values.paymentMeansCode, 'Payment means code');
  validateDate(values.issueDate, 'Issue date'); validateDate(values.dueDate, 'Due date');
  if (values.dueDate < values.issueDate) throw new Error('Due date cannot be before the issue date.');
  if (values.vatCategory === 'E') required(values.taxExemptionReason, 'Tax exemption reason');
  const supplier = party(values.seller, 'Seller'); const customer = party(values.buyer, 'Buyer');
  const totals = calculateInvoiceTotals(values); const taxRateBasisPoints = percentageToBasisPoints(values.vatRatePercent);
  const normalized = {
    schemaVersion: 'amoye-reviewed-invoice-facts-1', reviewedAt, invoiceNumber: values.invoiceNumber.trim(),
    issueDate: values.issueDate, dueDate: values.dueDate, invoiceTypeCode: '380' as const, currency: 'NGN' as const,
    buyerReference: values.buyerReference.trim(), paymentMeansCode: values.paymentMeansCode.trim(), supplier, customer,
    lines: [{ id: '1', itemName: values.description.trim(), quantity: values.quantity, unitCode: values.unitCode.trim(),
      unitPriceKobo: nairaToKoboExact(values.unitPriceNaira), lineExtensionAmountKobo: totals.lineExtensionAmountKobo,
      taxCategoryCode: values.vatCategory, taxRateBasisPoints,
      ...(values.vatCategory === 'E' ? { taxExemptionReason: values.taxExemptionReason.trim() } : {}) }],
    totals: { lineExtensionAmountKobo: totals.lineExtensionAmountKobo, taxExclusiveAmountKobo: totals.lineExtensionAmountKobo,
      taxAmountKobo: totals.taxAmountKobo, taxInclusiveAmountKobo: totals.payableAmountKobo,
      payableAmountKobo: totals.payableAmountKobo },
  };
  const reviewedFactSnapshotSha256 = await sha256(stableJson(normalized));
  const { schemaVersion: _schemaVersion, ...facts } = normalized;
  return { reviewedFactSnapshotSha256, ...facts };
}

export function formatKobo(value: string): string {
  const kobo = BigInt(value); const major = kobo / 100n; const minor = (kobo % 100n).toString().padStart(2, '0');
  return `₦${major.toLocaleString('en-NG')}.${minor}`;
}

function emptyParty(registrationName: string): InvoicePartyFormValues {
  return { registrationName, identifierScheme: '', electronicAddress: '', partyId: '', companyId: '', taxRegistrationId: '',
    streetName: '', cityName: '', postalZone: '', countryCode: 'NG' };
}

function party(value: InvoicePartyFormValues, label: string) {
  required(value.registrationName, `${label} legal name`); required(value.identifierScheme, `${label} identifier scheme`);
  required(value.electronicAddress, `${label} electronic address`); required(value.partyId, `${label} party identifier`);
  required(value.companyId, `${label} company identifier`); required(value.taxRegistrationId, `${label} tax registration identifier`);
  required(value.streetName, `${label} street address`); required(value.cityName, `${label} city`);
  if (!/^[A-Z]{2}$/.test(value.countryCode)) throw new Error(`${label} country must use a two-letter code.`);
  const scheme = value.identifierScheme.trim();
  return { endpointId: value.electronicAddress.trim(), endpointSchemeId: scheme, partyId: value.partyId.trim(),
    partyIdSchemeId: scheme, registrationName: value.registrationName.trim(), companyId: value.companyId.trim(),
    companyIdSchemeId: scheme, taxRegistrationId: value.taxRegistrationId.trim(),
    address: { streetName: value.streetName.trim(), cityName: value.cityName.trim(),
      ...(value.postalZone.trim() ? { postalZone: value.postalZone.trim() } : {}), countryCode: value.countryCode } };
}

function required(value: string, label: string): void { if (!value.trim()) throw new Error(`${label} is required.`); }
function validateDate(value: string, label: string): void {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value)
    throw new Error(`${label} must be a valid date.`);
}
async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`;
  return JSON.stringify(value);
}
